import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { client } from "../client";
import { useAuthStore } from "../useAuthStore";
import {
  CancelClubInvitationResponse,
  CLUB_INSIGHTS_QUERY_KEY,
  CLUB_INVITATIONS_QUERY_KEY,
  CLUB_MEMBERS_QUERY_KEY,
  ClubInsightsResponse,
  ClubInvitationsResponse,
  ClubMembersResponse,
  DemoteToMemberRequest,
  DemoteToMemberResponse,
  HandleInvitationRequest,
  HandleInvitationResponse,
  KickClubMemberResponse,
  LEAVE_CLUB_MUTATION_KEY,
  LeaveClubRequest,
  LeaveClubResponse,
  PATCH_CLUB_MUTATION_KEY,
  PatchClubRequest,
  PatchClubResponse,
  PromoteToAdminResponse,
  SendClubInvitationResponse,
  SENT_INVITATIONS_QUERY_KEY,
  SentClubInvitation,
  USER_CLUBS_QUERY_KEY,
  UserClubsResponse,
} from "./types";

/**
 * Hook to fetch and access club invitations for the authenticated user
 * @returns Query result containing club invitations data
 */
export function useClubInvitations() {
  return useQuery<ClubInvitationsResponse>({
    queryKey: CLUB_INVITATIONS_QUERY_KEY,
    queryFn: async () => {
      const response = await client.clubs.invitations.$get();

      if (!response.ok) {
        const errorData = await response.json();
        throw {
          message: errorData.message || "Failed to fetch club invitations",
          status: response.status,
        };
      }

      return await response.json();
    },
    // Don't run the query if there's no token
    enabled: !!localStorage.getItem("auth_token"),
  });
}

/**
 * Hook for handling club invitations (accept or decline)
 * @returns Mutation for handling club invitations
 */
export function useHandleClubInvitation() {
  const queryClient = useQueryClient();
  const setActiveClubId = useAuthStore((state) => state.setActiveClubId);

  return useMutation<
    HandleInvitationResponse,
    { message: string; status: number },
    HandleInvitationRequest
  >({
    mutationFn: async (data) => {
      const response = await client.clubs.invitations.$patch({
        json: data,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw {
          message: errorData.message || `Failed to ${data.action} invitation`,
          status: response.status,
        };
      }

      const responseData = await response.json();

      if (data.action === "accept") {
        setActiveClubId(responseData.clubId);
      }

      return responseData;
    },
    onSuccess: () => {
      // Invalidate and refetch club invitations
      queryClient.invalidateQueries({ queryKey: CLUB_INVITATIONS_QUERY_KEY });

      // Invalidate and refetch user clubs
      queryClient.invalidateQueries({ queryKey: USER_CLUBS_QUERY_KEY });
    },
  });
}

/**
 * Hook to fetch the user's clubs
 * @returns Query result containing user clubs data
 */
export function useUserClubs() {
  const activeClubId = useAuthStore((store) => store.activeClubId);
  const setActiveClubId = useAuthStore((store) => store.setActiveClubId);

  return useQuery<UserClubsResponse>({
    queryKey: USER_CLUBS_QUERY_KEY,
    queryFn: async () => {
      const response = await client.clubs.$get();

      if (!response.ok) {
        throw {
          message: "Failed to fetch user clubs",
          status: response.status,
        };
      }

      const data = await response.json();

      if (!activeClubId && data.length > 0) {
        setActiveClubId(data[0].id);
      }

      return data;
    },
    // Don't run the query if there's no token
    enabled: !!localStorage.getItem("auth_token"),
  });
}

/**
 * Hook to fetch the active club
 * @returns The active club data, or undefined if there is no active club
 */
export const useActiveClub = () => {
  const activeClubId = useAuthStore((state) => state.activeClubId);
  const { data: userClubs } = useUserClubs();

  // Don't need to initialize with a function that depends on potentially undefined data
  const [activeClub, setActiveClub] = useState<
    UserClubsResponse[0] | undefined
  >(undefined);

  useEffect(() => {
    // Update active club when either activeClubId or userClubs changes
    if (!activeClubId || !userClubs) {
      setActiveClub(undefined);
    } else {
      setActiveClub(
        userClubs.find((club) => club.id === activeClubId) || undefined
      );
    }
  }, [activeClubId, userClubs]);

  return activeClub;
};

/**
 * Hook for sending club invitations via email
 * @returns Mutation for sending club invitations
 */
export function useSendClubInvitation() {
  const queryClient = useQueryClient();
  const activeClub = useActiveClub();

  return useMutation<
    SendClubInvitationResponse,
    { message: string; status: number },
    { email: string }
  >({
    mutationFn: async ({ email }) => {
      if (!activeClub) {
        throw {
          message: "No active club selected",
          status: 400,
        };
      }

      const response = await client.clubs.invitations.$post({
        json: {
          clubId: activeClub.id,
          email: email,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw {
          message: errorData.message || "Failed to send invitation",
          status: response.status,
        };
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch sent invitations if that query exists
      queryClient.invalidateQueries({ queryKey: SENT_INVITATIONS_QUERY_KEY });
    },
  });
}

/**
 * Hook to fetch sent club invitations for the active club
 * @returns Query result containing sent club invitations data
 */
export function useSentClubInvitations() {
  const activeClub = useActiveClub();

  return useQuery<SentClubInvitation[], { message: string; status: number }>({
    queryKey: [...SENT_INVITATIONS_QUERY_KEY, activeClub?.id],
    queryFn: async () => {
      if (!activeClub) {
        return [];
      }

      const response = await client.clubs.invitations.sent.$get({
        query: {
          clubId: activeClub.id,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw {
          message: errorData.message || "Failed to fetch sent invitations",
          status: response.status,
        };
      }

      return await response.json();
    },
    // Don't run the query if there's no active club
    enabled: !!activeClub && !!localStorage.getItem("auth_token"),
    // Refresh data every 30 seconds
    refetchInterval: 30000,
  });
}

/**
 * Hook for cancelling a club invitation
 * @returns Mutation for cancelling a club invitation
 */
export function useCancelClubInvitation() {
  const queryClient = useQueryClient();

  return useMutation<
    CancelClubInvitationResponse,
    { message: string; status: number },
    { invitationId: string }
  >({
    mutationFn: async ({ invitationId }) => {
      const response = await client.clubs.invitations.$delete({
        json: {
          invitationId,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw {
          message: errorData.message || "Failed to cancel invitation",
          status: response.status,
        };
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch sent invitations
      queryClient.invalidateQueries({ queryKey: SENT_INVITATIONS_QUERY_KEY });
    },
  });
}

/**
 * Hook to fetch club members for the active club
 * @returns Query result containing club members data
 */
export function useClubMembers() {
  const activeClub = useActiveClub();

  return useQuery<ClubMembersResponse, { message: string; status: number }>({
    queryKey: [...CLUB_MEMBERS_QUERY_KEY, activeClub?.id],
    queryFn: async () => {
      if (!activeClub) {
        return [];
      }

      const response = await client.clubs.members.$get({
        query: {
          clubId: activeClub.id,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw {
          message: errorData.message || "Failed to fetch club members",
          status: response.status,
        };
      }

      return await response.json();
    },
    // Don't run the query if there's no active club
    enabled: !!activeClub && !!localStorage.getItem("auth_token"),
  });
}

/**
 * Hook for kicking a member from a club
 * @returns Mutation for kicking a member from a club
 */
export function useKickClubMember() {
  const queryClient = useQueryClient();
  const activeClub = useActiveClub();

  return useMutation<
    KickClubMemberResponse,
    { message: string; status: number },
    { userId: string }
  >({
    mutationFn: async ({ userId }) => {
      if (!activeClub) {
        throw {
          message: "No active club selected",
          status: 400,
        };
      }

      const response = await client.clubs.members.$delete({
        query: {
          clubId: activeClub.id,
          userId,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw {
          message: errorData.message || "Failed to kick member from club",
          status: response.status,
        };
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch club members
      queryClient.invalidateQueries({ queryKey: CLUB_MEMBERS_QUERY_KEY });
    },
  });
}

/**
 * Hook for promoting a member to admin
 * @returns Mutation for promoting a member to admin
 */
export function usePromoteToAdmin() {
  const queryClient = useQueryClient();
  const activeClub = useActiveClub();

  return useMutation<
    PromoteToAdminResponse,
    { message: string; status: number },
    { userId: string }
  >({
    mutationFn: async ({ userId }) => {
      if (!activeClub) {
        throw {
          message: "No active club selected",
          status: 400,
        };
      }

      const response = await client.clubs.admins.$post({
        json: {
          clubId: activeClub.id,
          userId,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw {
          message: errorData.message || "Failed to promote member to admin",
          status: response.status,
        };
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch club members
      queryClient.invalidateQueries({ queryKey: CLUB_MEMBERS_QUERY_KEY });
    },
  });
}

/**
 * Hook for demoting an admin to a regular member
 * @returns Mutation for demoting an admin to a regular member
 */
export function useDemoteToMember() {
  const queryClient = useQueryClient();
  const activeClubId = useAuthStore((store) => store.activeClubId);

  return useMutation<
    DemoteToMemberResponse,
    Error,
    Omit<DemoteToMemberRequest, "clubId">
  >({
    mutationFn: async ({ userId }) => {
      if (!activeClubId) {
        throw new Error("No active club selected");
      }

      const response = await client.clubs.admins.$delete({
        json: {
          clubId: activeClubId,
          userId,
        },
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(
          errorData.message || "Failed to demote admin to member"
        );
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({
        queryKey: [...CLUB_MEMBERS_QUERY_KEY, activeClubId],
      });
    },
  });
}

/**
 * Hook for leaving a club
 * @param options Optional parameters for the hook
 * @returns Mutation for leaving a club
 */
export function useLeaveClub() {
  const queryClient = useQueryClient();
  const activeClubId = useAuthStore((store) => store.activeClubId);

  return useMutation<LeaveClubResponse, Error, LeaveClubRequest>({
    mutationKey: LEAVE_CLUB_MUTATION_KEY,
    mutationFn: async ({ clubId }: LeaveClubRequest) => {
      // Use provided clubId or fall back to activeClubId
      const targetClubId = clubId || activeClubId;

      if (!targetClubId) {
        throw new Error("No club ID provided and no active club selected");
      }

      const response = await client.clubs.leave.$delete({
        json: {
          clubId: targetClubId,
        },
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData.message || "Failed to leave club");
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate user clubs query to refresh the clubs list
      queryClient.invalidateQueries({
        queryKey: USER_CLUBS_QUERY_KEY,
      });
    },
  });
}

/**
 * Hook for updating club details
 * @returns Mutation for updating club details
 */
export function usePatchClub() {
  const queryClient = useQueryClient();
  const activeClubId = useAuthStore((store) => store.activeClubId);

  return useMutation<PatchClubResponse, Error, PatchClubRequest>({
    mutationKey: PATCH_CLUB_MUTATION_KEY,
    mutationFn: async ({ name, icon, description }) => {
      if (!activeClubId) {
        throw new Error("No active club selected");
      }

      const response = await client.clubs[`:clubId`].$patch({
        param: { clubId: activeClubId },
        json: {
          name,
          icon,
          description,
        },
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData?.message || "Failed to update club details");
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate user clubs query to refresh the clubs list
      queryClient.invalidateQueries({
        queryKey: USER_CLUBS_QUERY_KEY,
      });
    },
  });
}

export function useClubInsights() {
  const activeClub = useActiveClub();

  return useQuery<ClubInsightsResponse>({
    queryKey: [...CLUB_INSIGHTS_QUERY_KEY, activeClub?.id],
    queryFn: async () => {
      if (!activeClub) throw new Error("No active club selected");
      const response = await client.clubs.insights.$get({
        query: { clubId: activeClub.id },
      });
      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData?.message || "Failed to fetch club insights");
      }
      return await response.json();
    },
    enabled: !!activeClub,
  });
}
